import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import Button from '../Button';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { colors } from '../theme/colors.stylex';
import { controlMetrics } from '../theme/controls.stylex';
import type { ButtonGroupProps } from './ButtonGroup.types';

const styles = stylex.create({
  group: { display: 'flex', outlineStyle: 'none', width: '100%', position: 'relative', flexWrap: 'wrap' },
  borderlessFocus: { borderWidth: { 'default': 0, ':focus': controlMetrics.borderWidth } },
  button: {
    marginLeft: '-1px',
    zIndex: { 'default': 'auto', ':focus': 40 },
    outlineStyle: 'none',
    borderTopLeftRadius: { 'default': 0, ':first-child': controlMetrics.radius },
    borderBottomLeftRadius: { 'default': 0, ':first-child': controlMetrics.radius },
    borderTopRightRadius: { 'default': 0, ':last-child': controlMetrics.radius },
    borderBottomRightRadius: { 'default': 0, ':last-child': controlMetrics.radius },
  },
});

const activeDisabledStyles = stylex.create({
  'outline': { backgroundColor: colors.surfaceSelected, borderColor: colors.accent },
  'theme': { backgroundColor: colors.accentHover },
  'danger': {},
  'theme-light': {},
  'no-border': { backgroundColor: colors.surfaceSelected },
  'navigation': { backgroundColor: colors.background, color: colors.link },
  'navigation-selected': { backgroundColor: colors.background, color: colors.link },
  'warning': {},
});

const activeStyles = stylex.create({
  'outline': {
    backgroundColor: {
      'default': colors.surfaceSelected,
      ':hover': colors.surfaceHover,
      ':focus': colors.surfacePressed,
      ':active': colors.surfacePressed,
    },
    borderColor: colors.accent,
  },
  'theme': {
    backgroundColor: {
      'default': colors.accentHover,
      ':hover': colors.accentHover,
      ':focus': colors.accentPressed,
      ':active': colors.accentPressed,
    },
  },
  'danger': {},
  'theme-light': {},
  'no-border': { backgroundColor: colors.surfaceSelected },
  'navigation': { backgroundColor: colors.background, color: colors.link },
  'navigation-selected': { backgroundColor: colors.background, color: colors.link },
  'warning': {},
});

/**
 * ButtonGroup.
 */
export const ButtonGroup = (props: ButtonGroupProps) => {
  const { id, extraClassNames = '', xstyle, testId, onChange, buttons, ...tooltipProps } = props;
  const tooltipData = getQTipData(tooltipProps);

  const styleProps = stylex.props(styles.group, xstyle);

  return (
    <div
      id={id ? `button-group-${id}` : undefined}
      data-testid={testId}
      className={clsx(styleProps.className, extraClassNames)}
      style={styleProps.style}
      {...tooltipData}>
      {buttons.filter(Boolean).map((item, index) => {
        if (item?.variant === 'button') {
          const { isActive, ...buttonPropsWithoutIsActive } = item.buttonProps;
          return (
            <Button
              key={index}
              {...buttonPropsWithoutIsActive}
              aria-pressed={isActive}
              xstyle={[
                styles.button,
                ['no-border', 'navigation', 'navigation-selected'].includes(item.buttonProps.variant ?? 'outline') &&
                  styles.borderlessFocus,
                isActive &&
                  (item.buttonProps.disabled ? activeDisabledStyles : activeStyles)[
                    item.buttonProps.variant ?? 'outline'
                  ],
                item.buttonProps.xstyle,
              ]}
              onClick={() => onChange && onChange(item.buttonProps.value)}
            />
          );
        }
        return item?.element;
      })}
    </div>
  );
};
